/**
 * Service worker registration.
 *
 * Only in production builds: in dev the worker would serve a stale shell over
 * Vite's HMR and turn every change into a mystery.
 *
 * When a new build is waiting, the app is told rather than reloaded from under
 * the user — someone mid-way through editing a day should decide when the page
 * refreshes.
 */

let updateListener: (() => void) | null = null;

export function onServiceWorkerUpdate(listener: () => void): () => void {
  updateListener = listener;
  return () => {
    updateListener = null;
  };
}

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        if (registration.waiting) updateListener?.();

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // `controller` is null on the very first install; that is a fresh
            // visit, not an update, and should not prompt anyone.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              updateListener?.();
            }
          });
        });
      })
      .catch(() => {
        // No offline support this session; the app works exactly as before.
      });
  });
}

/** Activate the waiting worker and reload — wired to the "обновить" prompt. */
export async function applyServiceWorkerUpdate(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    registration?.waiting?.postMessage('skip-waiting');
    // Reload once the new worker takes over, so the page matches its assets.
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
      once: true,
    });
  } catch {
    window.location.reload();
  }
}
